import { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Container } from "../components/Container";
import { useI18n } from "../i18n/LanguageProvider";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function Contact() {
  const { t, locale } = useI18n();
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (values) => {
    try {
      await axios.post(`${API}/contact`, values);
      setSent(true);
      reset();
      toast.success(t("contact.success"));
    } catch (e) {
      const msg = formatError(e.response?.data?.detail) || t("contact.error");
      toast.error(msg);
    }
  };

  const field =
    "w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-base text-black placeholder-black/30 outline-none transition-[border-color,box-shadow] duration-200 focus:border-green focus:shadow-[0_0_0_3px_rgba(77,215,154,0.18)]";

  return (
    <div className="bg-white" data-testid="contact-page">
      <Seo titleKey="meta.contact.title" descKey="meta.contact.description" path="/contact" pageType="contact" breadcrumbs={[{ name: t("footer.links.home"), path: "" }, { name: t("nav.contact"), path: "/contact" }]} />
      <Header />
      <Breadcrumbs items={[{ name: t("footer.links.home"), to: `/${locale}` }, { name: t("nav.contact") }]} />
      <main className="pt-10 md:pt-14">
        <Container>
          <div className="grid grid-cols-1 gap-16 pb-32 lg:grid-cols-2">
            <div>
              <Reveal>
                <h1 className="text-4xl font-semibold leading-tight tracking-tighter text-black sm:text-5xl lg:text-6xl">
                  {t("contact.title")}
                </h1>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-6 max-w-md text-lg leading-relaxed text-black/55">{t("contact.subtitle")}</p>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="mt-10 border-t border-black/5 pt-8">
                  <p className="text-sm text-black/40">{t("contact.directLabel")}</p>
                  <a
                    href="mailto:support@centauros.app"
                    data-testid="contact-direct-email"
                    className="mt-2 inline-block text-lg font-medium text-black underline decoration-green decoration-2 underline-offset-4 transition-colors hover:text-green"
                  >
                    support@centauros.app
                  </a>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.1}>
              {sent ? (
                <div
                  data-testid="contact-success"
                  className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-3xl border border-black/[0.07] p-12 text-center"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green/10">
                    <Check className="h-7 w-7 text-green" strokeWidth={1.5} />
                  </div>
                  <p className="mt-6 text-xl font-medium tracking-tight text-black">{t("contact.success")}</p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="mt-6 text-sm font-medium text-green hover:underline"
                  >
                    {t("contact.submit")} →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="contact-form" noValidate>
                  {/* honeypot */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                    {...register("company")}
                  />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-black/70">{t("contact.fields.name")}</label>
                      <input
                        className={field}
                        data-testid="contact-name"
                        placeholder={t("contact.placeholders.name")}
                        {...register("name", { required: true, minLength: 2 })}
                      />
                      {errors.name && <p className="mt-1.5 text-xs text-destructive">{t("contact.fields.name")}</p>}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-black/70">{t("contact.fields.email")}</label>
                      <input
                        type="email"
                        className={field}
                        data-testid="contact-email"
                        placeholder={t("contact.placeholders.email")}
                        {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })}
                      />
                      {errors.email && <p className="mt-1.5 text-xs text-destructive">{t("contact.fields.email")}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">{t("contact.fields.subject")}</label>
                    <input
                      className={field}
                      data-testid="contact-subject"
                      placeholder={t("contact.placeholders.subject")}
                      {...register("subject", { required: true, minLength: 2 })}
                    />
                    {errors.subject && <p className="mt-1.5 text-xs text-destructive">{t("contact.fields.subject")}</p>}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">{t("contact.fields.message")}</label>
                    <textarea
                      rows={5}
                      className={`${field} resize-none`}
                      data-testid="contact-message"
                      placeholder={t("contact.placeholders.message")}
                      {...register("message", { required: true, minLength: 10 })}
                    />
                    {errors.message && <p className="mt-1.5 text-xs text-destructive">{t("contact.fields.message")}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                    className="focus-ring inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient px-6 py-4 text-base font-medium text-white transition-[opacity,transform] duration-200 hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" /> {t("contact.sending")}
                      </>
                    ) : (
                      <>
                        {t("contact.submit")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
